import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, mergeProps as _mergeProps, vShow as _vShow, withDirectives as _withDirectives, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "block w-full" }
const _hoisted_2 = { class: "flex mb-4" }
const _hoisted_3 = /*#__PURE__*/_createElementVNode("span", { class: "icon-neu" }, null, -1)
const _hoisted_4 = [
  _hoisted_3
]
const _hoisted_5 = ["disabled"]
const _hoisted_6 = /*#__PURE__*/_createElementVNode("span", { class: "icon-loeschen" }, null, -1)
const _hoisted_7 = [
  _hoisted_6
]
const _hoisted_8 = ["disabled"]
const _hoisted_9 = /*#__PURE__*/_createElementVNode("span", { class: "icon-person" }, null, -1)
const _hoisted_10 = [
  _hoisted_9
]
const _hoisted_11 = { class: "ml-auto" }
const _hoisted_12 = { class: "text-center" }

import { onBeforeUnmount, onMounted, reactive, ref } from "vue";
import { BryntumGrid } from "@bryntum/grid-vue-3";
import { Menu, Store } from "@bryntum/grid";
import GridInputFilter from "@/components/GridInputFilter.vue";
import { useGlobalStore } from "@/stores/GlobalStore";
import {ErrorLocationType, PopupType, TableType} from '@/utilities/enums/Enums';
import { $t } from "@/utilities/i18n/config";
import ErrorHandler from "@/utilities/helpers/ErrorHandler";
import { GeneralResponse } from "@/models/api/Responses";
import Error from "@/components/Error.vue";
import {useI18n} from "vue-i18n";
import {checkboxColumnConfig, defaultSpaceAroundTableOnPage} from "@/AppConfig";
import LoadingSpinner from "@/components/LoadingSpinner.vue";
import {PermissionService} from "@/utilities/services/PermissionService";
import dialog from "@/dialog";
import {CoupledSystemSetting} from "@/models/system/CoupledSystemSetting";
import {CoupledSystemService} from "@/utilities/services/entities/CoupledSystemService";



export default /*#__PURE__*/_defineComponent({
  __name: 'CoupledSystemsTab',
  setup(__props) {

const gridStore = new Store();
const globalStore = useGlobalStore();

const { t } = useI18n();
const coupledSystemService = new CoupledSystemService();
const permissionService = new PermissionService();

let selectedCoupledSystems = ref([]);

const deleteSelection = async() => {
  //drop call without selection
  if(!selectedCoupledSystems.value?.some(()=>true)) return;

  const answer = await dialog
      .okText(t("global.delete"))
      .cancelText(t("global.cancel"))
      .confirm(t("settings.coupledSystems.confirmDeletionMulti", { count:selectedCoupledSystems.value.length}));

  if (answer) {
    let someEntriesFailed = false;
    for (const cs of selectedCoupledSystems.value){
      const resp = await coupledSystemService.deleteCoupledSystemSetting(cs.id);

      if(resp === null || resp === undefined || resp?.statusCode !== 0){
        ErrorHandler.handleError(resp, ErrorLocationType.table);
        someEntriesFailed = true;
        continue;
      }

      gridStore.applyChangeset({removed:[{id:cs.id}]});
    }

    if (someEntriesFailed) return;

    globalStore.showSuccess(t("global.deleted"));
  }
};

// during the test call the function looses the ref to the grid item, so we need to get it from the grid again
const updateTestCouplingColumn = (id:number, value:boolean) => {
  const found = gridStore.records.find(r => r.data.id === id);
  if(found === undefined) return;
  found.testCoupling = value;
}

const currentWindowHeight = ref("");
const recalcGridHeight = () => {
	currentWindowHeight.value = `${window.innerHeight-defaultSpaceAroundTableOnPage-80}px`;
};

const grid = ref(null);
const useGridConfig = {
  headerMenuFeature: false,
	relayStoreEvents: true,
  enableTextSelection: true,
  selectionMode : {
    row	  : true,
    checkbox: checkboxColumnConfig,
    showCheckAll : true,
    deselectFilteredOutRecords: true
  },
	columns : {
    data: [
		{ id: 'id', field: 'id', text: t('settings.coupledSystems.grid.id'), width: 100, },
		{ id: 'name', field: 'name', text: t('settings.coupledSystems.grid.name'), flex: 1, },
		{ id: 'baseUrl', field: 'baseUrl', text: t('settings.coupledSystems.grid.url'), flex: 1, },
		{ id: 'loginName', field: 'loginName', text: t('settings.coupledSystems.grid.loginName'), flex: 1, },
		{
      id: 'testCoupling',
      field: 'testCoupling',
			text: "",
			width   : 150,
			type: "template",
			template: ({record}) => {
				return record.data.testCoupling
            ? `<button class="primary" disabled>${t('settings.coupledSystems.grid.testingCoupling')}</button>`
            : `<button class="primary">${t('settings.coupledSystems.grid.testCoupling')}</button>`;
			},
		},
		{
      id: 'menu',
			text	: '',
			width   : 36,
			type	: 'widget',
			align: "center",
			hideable: false,
			resizable: false,
			sortable: false,
			filterable: false,
			widgets : [{
				type	 : 'button',
				width: 36,
				icon	 : 'icon-kontext_horizontal',
				onAction : ({ source: btn }) => {
					const menu = menuFactory(btn.cellInfo.record.data);
					menu.alignTo({ target: btn })
					menu.show();
				}
			}]
		},
	],
    listeners:{
      async add(event:any){
        if(event.records.length === 1 && event.isMove[event.records[0].id]){
          await globalStore.saveUserColumnOrder(TableType[TableType.coupledSystem], event.records[0].id, event.index.toString());
        }
      },
    }
  },
	store: gridStore,
	listeners: {
		selectionChange(event) {
			if(event.selection.length > 0 && event.selection[0]?.data === undefined) return;
      selectedCoupledSystems.value = selectedCoupledSystems.value.concat(event.selected.map(cr => cr.data))
      selectedCoupledSystems.value = selectedCoupledSystems.value.filter(c => !event.deselected.map(cr => cr.data).some(cr => c?.id === cr?.id));
		},
		datachange(){
			globalStore.currentItems = gridStore.count;
			globalStore.allItems = gridStore.originalCount;
		},
		async cellClick({column, record}) {
			if (column.data.field === 'testCoupling') {
        updateTestCouplingColumn(record.data.id, true);
        const coupledSystemResp = await coupledSystemService.getLocationsForForeignSystem(record.data.id);
        if (coupledSystemResp === null || coupledSystemResp === undefined || coupledSystemResp.statusCode !== 0){
          updateTestCouplingColumn(record.data.id, false);
          ErrorHandler.handleError(coupledSystemResp as GeneralResponse, ErrorLocationType.table);

          return;
        }

        updateTestCouplingColumn(record.data.id, false);
				globalStore.showSuccess(t('settings.coupledSystems.testSuccessful'));
			}
		},
	},
	height: currentWindowHeight,
}
const gridConfig = reactive(useGridConfig);

const menuFactory = (record:object) => {
	const menu = new Menu({
		anchor   : true,
		autoShow : true,
		items	: [
			{
				text: t("global.edit"),
				cls: 'color',
				icon: 'icon-editieren',
				weight: 100,
				onItem : ({ menu }) => {
					globalStore.openPopup({
						data: JSON.parse(JSON.stringify(menu.record)),
						action:addOrUpdateCoupledSystem,
						resetButton:false,
						title:$t("popups.settings.coupledSystems.edit"),
						type:PopupType.settingsEditCoupledSystems
					});
				}
      },
      {
        text   : t("global.delete"),
        disabled: !permissionService.userHasPermission("CoupledSystem.Delete"),
        hidden: false,
        cls	: 'color',
        icon: 'icon-loeschen',
        weight : 140,
        onItem: async ({ menu }) => {
          const answer = await dialog
              .okText(t("global.delete"))
              .cancelText(t("global.cancel"))
              .confirm(t("settings.coupledSystems.confirmDeletionSingle", {name: menu.record.name}));

          if (answer) {
            const resp = await coupledSystemService.deleteCoupledSystemSetting(menu.record.id);

            if(resp === null || resp === undefined || resp?.statusCode !== 0){
              ErrorHandler.handleError(resp, ErrorLocationType.table);
              return;
            }

            gridStore.applyChangeset({removed:[{id:menu.record.id}]});

            globalStore.showSuccess(t('global.deleted'));
          }
        }
			},
		],
	});

	menu.setRecord(record);

	return menu;
};


const newCoupledSystem = () => {
	globalStore.openPopup({
		data: new CoupledSystemSetting(),
		action: addOrUpdateCoupledSystem,
		resetButton:false,
		title:$t("popups.settings.coupledSystems.new"),
		type:PopupType.settingsEditCoupledSystems
	});
}

const resetCredentials = async () => {
	globalStore.openPopup({
		data: {},
		action: () => {},
		resetButton:false,
		title:$t("popups.settings.coupledSystems.resetPassword"),
		type:PopupType.settingsResetCredentials
	});
}

const addOrUpdateCoupledSystem = async () => {
	const coupledSystem = globalStore.modal.contentObject as CoupledSystemSetting;

	const coupledSystemResp = await coupledSystemService.addOrUpdateCoupledSystemSetting(coupledSystem);
	if (coupledSystemResp === null || coupledSystemResp === undefined || coupledSystemResp.statusCode !== 0){
		ErrorHandler.handleError(coupledSystemResp as GeneralResponse, ErrorLocationType.popup);
		return;
	}

	if(coupledSystem.id < 0) coupledSystem.id = coupledSystemResp.id;
  // fix selection when it was an update -> replace old value in selected-array
  else if(selectedCoupledSystems.value.some(el => el.id === coupledSystem.id)) {
    selectedCoupledSystems.value = selectedCoupledSystems.value.filter(el => el.id !== coupledSystem.id);
    selectedCoupledSystems.value.push(coupledSystem);
  }

	gridStore.add(coupledSystem);
	gridStore.sort(gridStore.sorters);

	globalStore.closeModal();
	globalStore.showSuccess(t('global.saved'));
}

const setGridData = async ()=>{
	// start spinner
	globalStore.gridDataIsLoading = true;

	const coupledSystemSettingsResp = await coupledSystemService.getCoupledSystemSettings();

	if (coupledSystemSettingsResp === null || coupledSystemSettingsResp === undefined || coupledSystemSettingsResp.statusCode !== 0) {
		ErrorHandler.handleError(coupledSystemSettingsResp, ErrorLocationType.table);
		return;
	}

  // add testCouplingProperty only used in this view and not important for other steps (doesn't come from backend so needs to be set here)
	gridStore.setStoreData(coupledSystemSettingsResp.settingInfo.map((cs)=>{
    cs.testCoupling = false;
    return cs
  }));

  globalStore.reorderColumns(TableType[TableType.coupledSystem]);

	globalStore.closeModal();
	// stop spinner
	globalStore.gridDataIsLoading = false;
};

onMounted(async () => {
	// calculate grid height and sub to window resize event
	recalcGridHeight();
	window.addEventListener('resize', recalcGridHeight);

	globalStore.setGridStore(ref(gridStore));
	globalStore.setGridColumnStore(ref(grid.value?.instance.value?.columns));

	await setGridData();
});

onBeforeUnmount(()=>{
	window.removeEventListener('resize', recalcGridHeight);

	globalStore.setGridStore(null);
	globalStore.setGridColumnStore(null);

	globalStore.currentItems = 0;
	globalStore.allItems = 0;
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _withDirectives(_createElementVNode("div", null, [
      _createVNode(Error, {
        errorLoc: _unref(ErrorLocationType).table
      }, null, 8, ["errorLoc"]),
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("button", {
            class: "primary mr-2",
            onClick: newCoupledSystem
          }, _hoisted_4),
          _createElementVNode("button", {
            class: "primary mr-2",
            disabled: !_unref(selectedCoupledSystems).some(()=>true),
            onClick: deleteSelection
          }, _hoisted_7, 8, _hoisted_5),
          _createElementVNode("button", {
            class: "special mr-2",
            disabled: !_unref(selectedCoupledSystems).some(()=>true),
            onClick: resetCredentials
          }, _hoisted_10, 8, _hoisted_8),
          _createElementVNode("div", _hoisted_11, [
            _createVNode(GridInputFilter, {
              gridStore: _unref(gridStore),
              hideFilterButton: true
            }, null, 8, ["gridStore"])
          ])
        ]),
        _createVNode(_unref(BryntumGrid), _mergeProps({
          ref_key: "grid",
          ref: grid
        }, gridConfig, { readOnly: true }), null, 16)
      ])
    ], 512), [
      [_vShow, !_unref(globalStore).gridDataIsLoading]
    ]),
    _withDirectives(_createElementVNode("div", _hoisted_12, [
      _createVNode(LoadingSpinner)
    ], 512), [
      [_vShow, _unref(globalStore).gridDataIsLoading]
    ])
  ], 64))
}
}

})